import {
  AGGREGATION_METHOD_OPTIONS,
  isRateAggregationMethod,
  requiresNumericAttribute,
  type AggregationMethod,
} from "../utils/traceConstants"
import { MAX_TRACE_AGENT_SCOPE_IDS } from "./customTraceAgentScope"

export interface CustomTraceAggregationOption {
  key: AggregationMethod
  label: string
  /** Set when the option can't be chosen; shown under the option label. */
  unavailableReason?: string
}

/**
 * What the attribute preview can say about the configured values. Anything
 * other than "numeric" means the preview can't confirm numeric data, which is
 * a reason to warn, never a reason to change the saved aggregation.
 */
export type CustomTracePreviewEvidence =
  | "pending"
  | "error"
  | "too_many_agents"
  | "no_spans"
  | "numeric"
  | "non_numeric"

export function getCustomTracePreviewEvidence({
  hasTooManyAgents,
  isPending,
  isError,
  matchedSpanCount,
  isNumeric,
}: {
  hasTooManyAgents: boolean
  isPending: boolean
  isError: boolean
  matchedSpanCount: number | undefined
  isNumeric: boolean | undefined
}): CustomTracePreviewEvidence {
  if (hasTooManyAgents) {
    return "too_many_agents"
  }
  if (isPending) {
    return "pending"
  }
  if (isError) {
    return "error"
  }
  if (!matchedSpanCount) {
    return "no_spans"
  }
  return isNumeric ? "numeric" : "non_numeric"
}

const NEEDS_ATTRIBUTE_REASON = "Choose a Metric Attribute first."
const RATE_FOR_DURATION_REASON = "Not available for Span duration."
const RATE_UNSUPPORTED_REASON =
  "Needs a true/false attribute, or spans with an OK or ERROR status, in recent traces."

function getUnavailableReason(
  method: AggregationMethod,
  {
    isDuration,
    hasMetricAttribute,
    hasRateSupport,
    hasValuesSupport,
  }: {
    isDuration: boolean
    hasMetricAttribute: boolean
    hasRateSupport: boolean
    hasValuesSupport: boolean
  },
): string | undefined {
  if (method === "values") {
    if (!hasValuesSupport) {
      return "Values isn't available in this workspace yet."
    }
    return isDuration
      ? "Values requires a span attribute."
      : hasMetricAttribute
        ? undefined
        : NEEDS_ATTRIBUTE_REASON
  }
  if (requiresNumericAttribute(method)) {
    return isDuration || hasMetricAttribute ? undefined : NEEDS_ATTRIBUTE_REASON
  }
  if (!isRateAggregationMethod(method)) {
    return undefined
  }
  if (isDuration) {
    return RATE_FOR_DURATION_REASON
  }
  return hasRateSupport ? undefined : RATE_UNSUPPORTED_REASON
}

/**
 * Every aggregation method, with a reason on the ones that can't be chosen.
 *
 * Numeric aggregations stay available for any attribute because the preview
 * only samples recent traces: no recent spans, an errored preview, or an agent
 * scope too large to preview says nothing about the attribute's type. They are
 * unavailable only when there is no attribute to aggregate.
 */
export function getCustomTraceAggregationOptions(context: {
  isDuration: boolean
  hasMetricAttribute: boolean
  hasRateSupport: boolean
  hasValuesSupport: boolean
}): CustomTraceAggregationOption[] {
  return AGGREGATION_METHOD_OPTIONS.map(({ key, label }) => {
    const unavailableReason = getUnavailableReason(key, context)
    return unavailableReason
      ? { key, label, unavailableReason }
      : { key, label }
  })
}

const NUMERIC_RUNTIME_RISK =
  "The metric reports an error for any value that isn't a number."

/**
 * Inline warning for the selected aggregation, or null when the configuration
 * looks sound. The selected method is shown as saved even when it is warned
 * about; the user decides whether to change it.
 */
export function getCustomTraceAggregationWarning({
  method,
  options,
  isDuration,
  metricAttribute,
  spanName,
  evidence,
}: {
  method: AggregationMethod
  options: CustomTraceAggregationOption[]
  isDuration: boolean
  metricAttribute: string
  spanName: string
  evidence: CustomTracePreviewEvidence
}): string | null {
  const option = options.find(({ key }) => key === method)
  const label = option?.label ?? method

  if (method === "values") {
    return option?.unavailableReason ?? null
  }

  if (requiresNumericAttribute(method) && !isDuration) {
    if (!metricAttribute) {
      return `${label} needs a Metric Attribute. Choose one, or switch to Count.`
    }
    switch (evidence) {
      case "too_many_agents":
        return `Preview is off because more than ${MAX_TRACE_AGENT_SCOPE_IDS} agents are selected, so Coval can't check that ${metricAttribute} is numeric. ${NUMERIC_RUNTIME_RISK}`
      case "error":
        return `Preview is unavailable, so Coval can't check that ${metricAttribute} is numeric. ${NUMERIC_RUNTIME_RISK}`
      case "no_spans":
        return `No recent ${spanName} spans have ${metricAttribute}, so Coval can't check that it's numeric. ${NUMERIC_RUNTIME_RISK}`
      case "non_numeric":
        return `Recent values of ${metricAttribute} aren't all numbers. ${NUMERIC_RUNTIME_RISK}`
      case "pending":
      case "numeric":
        return null
    }
  }

  if (!isRateAggregationMethod(method) || !option?.unavailableReason) {
    return null
  }
  if (isDuration) {
    return `${label} isn't available for Span duration. Choose another aggregation.`
  }
  return evidence === "pending"
    ? null
    : `Coval can't confirm ${label} works for this span. It needs a true/false attribute, or spans with an OK or ERROR status, in recent traces.`
}
